import { Show } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Select from '~/components/Select'
import Textarea from '~/components/Textarea'
import type { TabCotizacionesProps } from './types'

/** Pestaña Cotizaciones y PDF: marca del documento, quién emite y valores por defecto. */
function TabCotizaciones(props: TabCotizacionesProps) {
  const { form, logoUrl, handleLogoFile, currencyOptions } = props

  return (
    <Card>
      <Card.Title>
        <div class="flex items-center">
          <Icon icon="material-symbols-light:description-outline" size={20} class="mr-2" />
          <h2 class="text-xl font-semibold">Cotizaciones y PDF</h2>
        </div>
      </Card.Title>
      <Card.Body>
        <p class="text-sm text-foreground-subtle mb-4">Con esto sale el PDF de tus propuestas y con esto se precarga cada cotización nueva.</p>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div class="space-y-4">
            <h3 class="text-sm font-semibold text-foreground">Marca del PDF</h3>

            {/* El logo va en la cabecera y, en grande y translúcido, como
                marca de agua de cada página. */}
            <div class="flex items-center gap-3">
              <div class="size-16 shrink-0 rounded-lg border border-border bg-surface-muted flex items-center justify-center overflow-hidden">
                <Show when={logoUrl()} fallback={<Icon icon="material-symbols-light:image-outline" size={26} class="text-foreground-subtle" />}>
                  <img src={logoUrl()} alt="Logo de la propuesta" class="max-h-full max-w-full object-contain" />
                </Show>
              </div>
              <div class="flex flex-wrap gap-2">
                {/* La etiqueta abre el selector de forma nativa: llamar a
                    click() sobre un input oculto no funciona en todos los
                    navegadores. */}
                <label class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-info-soft px-3 py-2 text-sm font-medium text-info transition-colors hover:bg-info-soft/70">
                  <Icon icon="material-symbols-light:upload" size={20} />
                  Subir logo
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    class="sr-only"
                    onClick={e => {
                      e.currentTarget.value = ''
                    }}
                    onChange={e => handleLogoFile(e.currentTarget.files?.[0])}
                  />
                </label>
                <Show when={logoUrl()}>
                  <Button size="sm" variant="text" color="danger" onClick={() => form.setValue('brandingLogoUrl', '')} icon="material-symbols-light:delete-outline">
                    Quitar
                  </Button>
                </Show>
              </div>
            </div>
            <p class="text-xs text-foreground-subtle">PNG o JPG, hasta 1 MB. Sin logo propio se usa el isotipo de la marca.</p>

            <Input name="brandingFooterText" label="Texto del pie de página" placeholder="© Tu marca. Todos los derechos reservados." />
          </div>

          <div class="space-y-4">
            <h3 class="text-sm font-semibold text-foreground">Quién emite</h3>
            <Input name="issuerName" label="Nombre o razón social" placeholder={(form.values().name as string) || 'Tu nombre'} />
            <Input name="issuerDocument" label="Documento / NIT" />
            <Input name="issuerPhone" label="Teléfono" />
            <Input name="issuerEmail" label="Correo" type="email" />
            <Input name="issuerAddress" label="Dirección" />
          </div>

          <div class="space-y-4">
            <h3 class="text-sm font-semibold text-foreground">Valores por defecto</h3>
            <Select name="defaultsCurrency" label="Moneda de la cotización" value={(form.values().defaultsCurrency as string) ?? 'COP'} options={currencyOptions()} />
            <Input name="defaultsTaxRate" label="Impuesto por defecto (%)" type="number" min="0" step="0.01" />
            <Input name="defaultsValidDays" label="Vigencia (días)" type="number" min="0" />
            <Textarea name="defaultsTerms" label="Términos y condiciones por defecto" rows={4} placeholder="Forma de pago, tiempos de entrega, alcance..." />
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

export default TabCotizaciones
